import { DefaultBrowserBehavior } from "amazon-chime-sdk-js";
import type { TrackingMetadata } from "../videoMeeting.types";

declare global {
  interface Window {
    posthog?: {
      get_session_id?: () => string;
      /** The identity events are recorded under, used to join a submission to the visit that produced it. */
      get_distinct_id?: () => string;
      config?: { session_id?: string };
      session_id?: string;
    };
  }
}

const getPostHogSessionId = (): string => {
  // unchanged — keep existing implementation
  try {
    if (typeof window !== "undefined" && window.posthog) {
      if (typeof window.posthog.get_session_id === "function") {
        const sessionId = window.posthog.get_session_id();
        if (sessionId) return sessionId;
      }
      if (window.posthog.config?.session_id) return window.posthog.config.session_id;
      if (window.posthog.session_id) return window.posthog.session_id;
    }
  } catch {
    // PostHog unavailable
  }
  return `fallback_session_${Math.floor(Date.now() / 1000)}`;
};

type ParsedAttribution = {
  browser_name: string;
  browser_version: string;
  os_name: string;
  os_version: string;
};

const EMPTY_ATTRIBUTION: ParsedAttribution = {
  browser_name: "",
  browser_version: "",
  os_name: "",
  os_version: "",
};

const parseBrowser = (): { name: string; version: string } => {
  try {
    const b = new DefaultBrowserBehavior();
    return {
      name: (b.name() ?? "").slice(0, 100),
      // Full version string (e.g. "147.0.0.0") to match sdk_attributes.browserVersion
      // captured on sdk_meetingStartSucceeded events. Use the method name verified
      // in Step 1.5 — most SDK versions expose this as `version()`.
      version: (b.version() ?? "").slice(0, 100),
    };
  } catch {
    return { name: "", version: "" };
  }
};

const parseOs = (ua: string): { name: string; version: string } => {
  // ORDER MATTERS: iPhone/iPad/iPod UAs contain the substring "Mac OS X", so
  // iOS must be checked BEFORE macOS or iPhones get misclassified as macOS.
  // Note: iPadOS 13+ identifies as "Mac OS X" by default (Apple's choice — no
  // "iPad" substring). iPads will be classified as macOS. Accepted miss.
  if (/iPhone|iPad|iPod/i.test(ua)) {
    const m = ua.match(/iPhone OS (\d+(?:_\d+){1,2})/i) || ua.match(/CPU OS (\d+(?:_\d+){1,2})/i);
    return { name: "iOS", version: (m?.[1] ?? "").replace(/_/g, ".").slice(0, 100) };
  }
  if (/Android/i.test(ua)) {
    const m = ua.match(/Android (\d+(?:\.\d+){0,2})/i);
    return { name: "Android", version: (m?.[1] ?? "").slice(0, 100) };
  }
  if (/Windows NT/i.test(ua)) {
    const m = ua.match(/Windows NT (\d+\.\d+)/i);
    // Windows NT 10.0 → "10"; NT 6.3 → "8.1"; NT 6.2 → "8"; NT 6.1 → "7".
    const raw = m?.[1] ?? "";
    const friendly =
      raw === "10.0" ? "10" :
      raw === "6.3" ? "8.1" :
      raw === "6.2" ? "8" :
      raw === "6.1" ? "7" :
      raw;
    return { name: "Windows", version: friendly.slice(0, 100) };
  }
  if (/Mac OS X/i.test(ua)) {
    const m = ua.match(/Mac OS X (\d+(?:[._]\d+){1,2})/i);
    return { name: "macOS", version: (m?.[1] ?? "").replace(/_/g, ".").slice(0, 100) };
  }
  if (/CrOS/i.test(ua)) {
    return { name: "ChromeOS", version: "" };
  }
  if (/Linux/i.test(ua)) {
    return { name: "Linux", version: "" };
  }
  return { name: "", version: "" };
};

const parseAttribution = (): ParsedAttribution => {
  if (typeof navigator === "undefined") return EMPTY_ATTRIBUTION;
  try {
    const browser = parseBrowser();
    const os = parseOs(navigator.userAgent ?? "");
    return {
      browser_name: browser.name,
      browser_version: browser.version,
      os_name: os.name,
      os_version: os.version,
    };
  } catch {
    return EMPTY_ATTRIBUTION;
  }
};

export const getTrackingMetadata = (
  user: { id: number } | null
): TrackingMetadata => {
  const attribution = parseAttribution();
  return {
    user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
    user_id: user?.id ?? 0,
    posthog_session_id: getPostHogSessionId(),
    ...attribution,
  };
};
